import { useEffect, useState } from "react";
import { Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import { Header } from "../../components/laxmi/Chrome";
import { api, err, fileUrl, money } from "../../lib/api";
import { useSite } from "../../lib/site";

const AMOUNTS = [100, 300, 500, 1000, 2000, 5000, 10000, 15000, 20000];

function OfferBox({ offer }) {
  if (!offer) return null;
  const total = offer.deposit_amount + offer.bonus_amount;
  return (
    <div className="offer-box" data-testid="deposit-offer-box">
      <Gift /><div><b>{offer.title} applied</b>
        <small>Deposit {money(offer.deposit_amount)} + Bonus {money(offer.bonus_amount)} = Balance <strong>{money(total)}</strong></small>
        <small>Wagering {offer.wager_multiplier}x of {money(total)} = <strong data-testid="deposit-offer-wager">{money(offer.wagering_required)}</strong> before withdrawal</small></div>
    </div>
  );
}

export default function Deposit({ data, setPage, onDone }) {
  const { site } = useSite();
  const [amount, setAmount] = useState("500"), [utr, setUtr] = useState(""), [busy, setBusy] = useState(false), [offers, setOffers] = useState([]);
  useEffect(() => { api.get("/deposit-offers").then(r => setOffers(r.data)).catch(() => {}); }, []);
  const [method, setMethod] = useState("UPI QR");
  const ready = method === "UPI QR" ? site.qr_url || site.upi_id : site.bank_account_number;
  const offerFor = n => offers.find(o => o.deposit_amount === Number(n));
  const copy = (v, label) => { navigator.clipboard?.writeText(v); toast.success(`${label} copied`); };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/deposit", { method, amount: Number(amount), utr });
      toast.success("Deposit submitted. Balance will be added after admin approval.");
      setUtr(""); await onDone(); setPage("history");
    } catch (e) { toast.error(err(e)); }
    setBusy(false);
  };
  return (
    <div className="screen">
      <Header title="Deposit" onBack={() => setPage("account")} onBell={() => setPage("notifications")} />
      <section className="balance-card compact"><span>💰 Balance</span><strong data-testid="deposit-balance">{money(data.wallet.balance)}</strong></section>
      {offers.length > 0 && <div className="offer-strip">{offers.map(o => <button key={o.offer_id} onClick={() => setAmount(String(o.deposit_amount))} data-testid={`offer-strip-${o.deposit_amount}`}><b>Deposit {money(o.deposit_amount)}</b><span>+{money(o.bonus_amount)} Bonus</span><small>{o.wager_multiplier}x wager</small></button>)}</div>}
      <div className="filter-row pay-methods">{[["UPI QR", "UPI / QR"], ["Bank Transfer", "Bank transfer"]].map(([m, l]) => <button key={m} className={method === m ? "selected" : ""} onClick={() => setMethod(m)} data-testid={`deposit-method-${m === "UPI QR" ? "upi" : "bank"}`}>{l}</button>)}</div>
      {method === "UPI QR" ? <section className="action-panel qr-panel" data-testid="payment-qr-panel">
        <h2>Scan & pay</h2>
        {!ready ? <p className="demo-note" data-testid="qr-not-configured">Payment QR is not set up yet. Please contact support.</p> : <>
          {site.qr_url && <img className="qr-image" src={fileUrl(site.qr_url)} alt="Payment QR" data-testid="payment-qr-image" />}
          {site.upi_id && <div className="upi-row"><div><small>UPI ID</small><b data-testid="payment-upi-id">{site.upi_id}</b>{site.payee_name && <small>{site.payee_name}</small>}</div>
            <button onClick={() => copy(site.upi_id, "UPI ID")} data-testid="copy-upi-button"><Copy size={16} /> Copy</button></div>}
          <ol className="pay-steps"><li>Scan the QR with any UPI app and pay the amount.</li><li>Enter the same amount and the 12-digit UTR / reference number.</li><li>Balance is added after verification.</li></ol>
        </>}
      </section> : <section className="action-panel" data-testid="bank-details-panel">
        <h2>Bank transfer</h2>
        {!ready ? <p className="demo-note" data-testid="bank-not-configured">Bank transfer is not set up yet. Please contact support.</p> : <>
          {[["Bank name", site.bank_name, "bank-name"], ["Account holder", site.bank_account_holder, "bank-holder"], ["Account number", site.bank_account_number, "bank-account"], ["IFSC", site.bank_ifsc, "bank-ifsc"]].map(([l, v, id]) => v ? (
            <div className="upi-row" key={id}><div><small>{l}</small><b data-testid={`deposit-${id}`}>{v}</b></div><button onClick={() => copy(v, l)} data-testid={`copy-${id}-button`}><Copy size={16} /> Copy</button></div>) : null)}
          <ol className="pay-steps"><li>Transfer the amount by IMPS / NEFT / RTGS to the account above.</li><li>Enter the same amount and the bank UTR / reference number.</li><li>Balance is added after verification.</li></ol>
        </>}
      </section>}
      <section className="action-panel">
        <h2>Deposit amount</h2>
        <div className="amounts">{AMOUNTS.map(n => { const o = offerFor(n); return (
          <button className={`${Number(amount) === n ? "selected" : ""} ${o ? "has-offer" : ""}`} onClick={() => setAmount(String(n))} key={n} data-testid={`amount-${n}-button`}>
            {o && <em className="offer-tag">+{money(o.bonus_amount)}</em>}₹{n.toLocaleString("en-IN")}</button>); })}</div>
        <input data-testid="deposit-amount-input" value={amount} onChange={e => setAmount(e.target.value)} type="number" placeholder="Amount" />
        <OfferBox offer={offerFor(amount)} />
        <input data-testid="deposit-utr-input" value={utr} onChange={e => setUtr(e.target.value)} placeholder="UTR / Reference number" className="utr-input" />
        <p className="demo-note">Limit {money(site.min_deposit)} – {money(site.max_deposit)} · Wrong UTR will be rejected</p>
        <button className="primary-button" onClick={submit} disabled={busy || !ready || utr.trim().length < 6} data-testid="deposit-submit-button">{busy ? "Submitting…" : "Submit deposit"}</button>
      </section>
    </div>
  );
}
